/*
 * PDP V1 — one state model for the Product information (V1) section.
 *
 * Owns: option selection -> the form's single hidden variant id, price, CTA
 * state, the add-to-cart request (exactly one handler, on the form's submit
 * event), the mobile sticky bar, the size guide dialog, and "Purchase area"
 * tile links elsewhere on the page.
 *
 * Cart path matches assets/product-form.js: POST the whole form to the cart,
 * then open the Amp Slide Cart via SLIDECART_UPDATE/SLIDECART_OPEN, falling
 * back to /cart only if Amp never loads (CLAUDE.md, "Cart drawer").
 */
(function () {
  'use strict';

  var instances = [];
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var mobileQuery = window.matchMedia('(max-width: 989px)');

  function qs(root, sel) { return root.querySelector(sel); }
  function qsa(root, sel) { return Array.prototype.slice.call(root.querySelectorAll(sel)); }

  function headerOffset() {
    var total = 0;
    qsa(document, '.shopify-section-group-header-group, #shopify-section-header').forEach(function (el) {
      var cs = getComputedStyle(el);
      if (cs.position === 'sticky' || cs.position === 'fixed') {
        var r = el.getBoundingClientRect();
        if (r.bottom > 0) total = Math.max(total, r.bottom);
      }
    });
    return total + 12;
  }

  function scrollToEl(el) {
    if (!el) return;
    var top = el.getBoundingClientRect().top + window.pageYOffset - headerOffset();
    window.scrollTo({ top: Math.max(0, top), behavior: reduceMotion.matches ? 'auto' : 'smooth' });
  }

  function isShown(el) {
    if (!el || !el.isConnected) return false;
    var cs = getComputedStyle(el);
    if (cs.display === 'none' || cs.visibility === 'hidden' || parseFloat(cs.opacity) < 0.05) return false;
    var r = el.getBoundingClientRect();
    return r.width > 0 && r.height > 0 && r.bottom > 0 && r.top < window.innerHeight;
  }

  // Anything that should take the bottom of the screen away from the sticky bar.
  var OVERLAY_SELECTORS = [
    '.slidecarthq-overlay',            // Amp cart drawer backdrop
    'dialog[open]',                    // size guide and any native dialog
    '.size-chart-popup.active',
    '.timesact_bis_popup_overlay',     // back-in-stock popup
    'csm-cookie-consent',              // consent banner
    'menu-drawer details[open] > .menu-drawer',
    '.menu-drawer-container[open]',
    '.drawer.active', '.modal.is-open'
  ];
  function overlayOpen() {
    for (var i = 0; i < OVERLAY_SELECTORS.length; i++) {
      var els = document.querySelectorAll(OVERLAY_SELECTORS[i]);
      for (var j = 0; j < els.length; j++) {
        if (els[j].closest('[data-pdp-v1-sticky]')) continue;
        if (OVERLAY_SELECTORS[i] === 'csm-cookie-consent') {
          // The consent host is a permanent, transparent full-screen layer;
          // only a visible banner inside its shadow root counts.
          var sr = els[j].shadowRoot;
          if (sr && Array.prototype.some.call(sr.querySelectorAll('*'), function (el) {
            var r = el.getBoundingClientRect();
            return r.height > 40 && r.width > 100 && isShown(el);
          })) return true;
          continue;
        }
        if (isShown(els[j])) return true;
      }
    }
    // Scroll lock by a menu or drawer.
    if (getComputedStyle(document.body).overflow === 'hidden') return true;
    return false;
  }

  function openCart() {
    var start = Date.now();
    (function tryOpen() {
      if (typeof window.SLIDECART_UPDATE === 'function' && typeof window.SLIDECART_OPEN === 'function') {
        window.SLIDECART_UPDATE(function () { window.SLIDECART_OPEN(); });
      } else if (Date.now() - start < 6000) {
        setTimeout(tryOpen, 150);
      } else {
        window.location = (window.routes && window.routes.cart_url) || '/cart';
      }
    })();
  }

  function PdpV1(root) {
    this.root = root;
    this.cleanup = [];
    var dataEl = qs(root, 'script[data-pdp-v1-product]');
    this.data = JSON.parse(dataEl.textContent);
    // Liquid's t filter HTML-escapes (e.g. isn&#39;t); decode for textContent.
    var decoder = document.createElement('textarea');
    this.strings = {};
    for (var k in this.data.strings) { decoder.innerHTML = this.data.strings[k]; this.strings[k] = decoder.value; }
    this.form = document.getElementById(root.getAttribute('data-form-id'));
    this.idInput = this.form && qs(this.form, '[data-pdp-v1-id]');
    this.cta = this.form && qs(this.form, '[data-pdp-v1-cta]');
    this.ctaLabel = this.cta && qs(this.cta, '[data-pdp-v1-label]');
    this.msg = this.form && qs(this.form, '[data-pdp-v1-msg]');
    this.defaultLabel = (this.cta && this.cta.getAttribute('data-default-label')) || this.strings.addToCart;
    this.picker = qs(root, '[data-pdp-v1-picker]');
    this.termsId = qs(root, '[data-pdp-v1-terms-id]');
    this.express = this.form && qs(this.form, '[data-pdp-v1-express]');
    this.recipient = this.form && qs(this.form, '[data-pdp-v1-recipient]');
    this.priceEl = qs(root, '[data-pdp-v1-price]');
    this.sticky = qs(root, '[data-pdp-v1-sticky]');
    this.pending = false;
    this.userTouched = false;
    this.timers = [];

    this.groups = this.picker ? qsa(this.picker, '[data-option-index]').filter(function (el) {
      return el.classList.contains('pdp-v1__option');
    }) : [];
    this.sizeIndex = -1;
    this.colorIndex = -1;
    this.otherIndexes = [];
    var self = this;
    this.groups.forEach(function (g) {
      var i = parseInt(g.getAttribute('data-option-index'), 10);
      var kind = g.getAttribute('data-option-kind');
      if (kind === 'size') self.sizeIndex = i;
      else if (kind === 'color') self.colorIndex = i;
      else self.otherIndexes.push(i);
    });
    // "Choose a size" only where there is a size to choose.
    this.chooseLabel = this.sizeIndex > -1 ? this.strings.chooseSize : this.strings.chooseOption;

    this.selected = this.readSelection();
    this.bind();
    this.setupRecipient();
    this.watchEmptyAppBlocks();
    this.update({ initial: true });
    this.setupSticky();
    this.setupGuide();
  }

  // App blocks (e.g. Kaching Bundles with no active deal) render an empty
  // wrapper that still takes a flex gap. Cancel the gap while it is empty;
  // restore it automatically if the app fills it later.
  PdpV1.prototype.watchEmptyAppBlocks = function () {
    if (!('ResizeObserver' in window)) return;
    var self = this;
    var ro = new ResizeObserver(function (entries) {
      entries.forEach(function (en) {
        en.target.classList.toggle('pdp-v1-empty', en.contentRect.height < 1);
      });
    });
    qsa(this.root, '.pdp-v1__buy > .blocks-section').forEach(function (el) { ro.observe(el); });
    this.cleanup.push(function () { ro.disconnect(); });
  };

  PdpV1.prototype.on = function (target, type, fn, opts) {
    target.addEventListener(type, fn, opts);
    this.cleanup.push(function () { target.removeEventListener(type, fn, opts); });
  };

  PdpV1.prototype.readSelection = function () {
    var sel = [];
    for (var i = 0; i < this.data.options.length; i++) sel.push(null);
    this.groups.forEach(function (g) {
      var i = parseInt(g.getAttribute('data-option-index'), 10);
      var checked = qs(g, 'input[type=radio]:checked');
      sel[i] = checked ? checked.value : null;
    });
    // No V1 picker on the page (removed in the editor, or a single-variant
    // product): use the form's server-rendered variant, else the first available,
    // so the button always works.
    if (!this.groups.length) {
      var idNow = this.idInput && this.idInput.value;
      var v = this.data.variants.find(function (x) { return String(x.id) === String(idNow); }) ||
        this.data.variants.find(function (x) { return x.available; }) || this.data.variants[0];
      return v ? v.options.slice() : sel;
    }
    return sel;
  };

  PdpV1.prototype.complete = function () {
    return this.selected.every(function (v) { return v !== null; });
  };

  PdpV1.prototype.findVariant = function (sel) {
    return this.data.variants.find(function (v) {
      return v.options.every(function (o, i) { return sel[i] === o; });
    }) || null;
  };

  // Best variant to represent a partial selection (for price and gallery).
  PdpV1.prototype.representative = function () {
    var sel = this.selected;
    var matches = this.data.variants.filter(function (v) {
      return v.options.every(function (o, i) { return sel[i] === null || sel[i] === o; });
    });
    return matches.find(function (v) { return v.available; }) || matches[0] || this.data.variants[0];
  };

  PdpV1.prototype.bind = function () {
    var self = this;
    if (this.picker) {
      this.on(this.picker, 'change', function (e) {
        var input = e.target;
        if (!input.matches('input[type=radio]')) return;
        var i = parseInt(input.getAttribute('data-option-index'), 10);
        var prev = self.selected[i];
        self.selected[i] = input.value;
        self.userTouched = true;
        self.justAdded = false;
        clearTimeout(self.addedTimer);
        self.update({ changedIndex: i, colorChanged: i === self.colorIndex && prev !== input.value });
      });
    }
    if (this.cta) {
      this.on(this.cta, 'click', function (e) { e.preventDefault(); self.submit(); });
    }
    if (this.form) {
      // Enter-key or programmatic submits: keep Amp's document-level submit
      // hook out of it, so the add happens exactly once, here.
      this.on(this.form, 'submit', function (e) { e.preventDefault(); e.stopPropagation(); self.submit(); });
    }
  };

  /* ---------- Gift card recipient ---------- */

  // Fields stay disabled (never submitted) until "send as a gift" is ticked.
  PdpV1.prototype.setupRecipient = function () {
    var r = this.recipient;
    if (!r) return;
    var toggle = qs(r, '[data-recipient-toggle]');
    var fields = qs(r, '[data-recipient-fields]');
    var offset = qs(r, '[data-recipient-offset]');
    if (offset) offset.value = String(new Date().getTimezoneOffset());
    var sendOn = qs(r, '[data-recipient-sendon]');
    if (sendOn) {
      // Shopify accepts a send date from today up to 90 days out.
      var d = new Date();
      var iso = function (x) { return new Date(x.getTime() - x.getTimezoneOffset() * 60000).toISOString().slice(0, 10); };
      sendOn.min = iso(d);
      sendOn.max = iso(new Date(d.getTime() + 90 * 86400000));
    }
    var sync = function () {
      var on = toggle.checked;
      fields.hidden = !on;
      qsa(fields, 'input, textarea').forEach(function (el) { el.disabled = !on; });
    };
    this.on(toggle, 'change', sync);
    sync();
  };

  // Returns false (and says why) when the recipient email is missing or invalid.
  PdpV1.prototype.recipientValid = function () {
    var r = this.recipient;
    if (!r) return true;
    var toggle = qs(r, '[data-recipient-toggle]');
    if (!toggle || !toggle.checked) return true;
    var email = qs(r, '[data-recipient-email]');
    if (email && email.value.trim() && email.checkValidity()) return true;
    this.setMessage(this.strings.recipientEmail || 'Please enter a valid recipient email.', true);
    if (email) { email.setAttribute('aria-invalid', 'true'); email.focus(); }
    return false;
  };

  PdpV1.prototype.update = function (opts) {
    opts = opts || {};
    var self = this;
    var variant = this.complete() ? this.findVariant(this.selected) : null;
    this.variant = variant;
    var rep = variant || this.representative();

    if (this.idInput) this.idInput.value = variant ? variant.id : '';
    // Instalment message: follows the chosen variant, or the representative one
    // before a size is picked, so the amount always matches the price shown.
    if (this.termsId) {
      var termsVariant = variant || rep;
      if (termsVariant && String(this.termsId.value) !== String(termsVariant.id)) {
        this.termsId.value = termsVariant.id;
        var terms = qs(this.root, 'shopify-payment-terms');
        if (terms) terms.setAttribute('variant-id', termsVariant.id);
      }
    }
    // Native express checkout: only once a real, buyable variant is chosen.
    if (this.express) this.express.hidden = !(variant && variant.available);

    // Selected-value labels next to each option name.
    this.groups.forEach(function (g) {
      var i = parseInt(g.getAttribute('data-option-index'), 10);
      var lbl = qs(g, '[data-selected-label]');
      if (lbl) lbl.textContent = self.selected[i] ? ': ' + self.selected[i] : '';
    });

    this.markAvailability();

    // Price: the chosen variant, or the representative of a partial choice.
    if (this.priceEl) {
      var cur = qs(this.priceEl, '[data-price]');
      var cmpWrap = qs(this.priceEl, '[data-compare-wrap]');
      var cmp = qs(this.priceEl, '[data-compare]');
      var onSale = rep.compare_at_price > rep.price;
      if (cur) { cur.innerHTML = rep.priceHtml; cur.classList.toggle('is-sale', onSale); }
      if (cmpWrap) cmpWrap.hidden = !onSale;
      if (cmp) cmp.innerHTML = onSale ? rep.compareHtml : '';
    }

    // Size message: explain, never silently switch the shopper's size.
    var sizeMsg = this.picker && qs(this.picker, '[data-pdp-v1-size-msg]');
    if (sizeMsg) {
      if (this.complete() && (!variant || !variant.available) && this.sizeIndex > -1) {
        // Size-only products (e.g. The Hef) have no colour to name.
        sizeMsg.textContent = (this.colorIndex > -1 ? this.strings.sizeUnavailable : this.strings.sizeSoldOut)
          .replace('[size]', this.selected[this.sizeIndex])
          .replace('[color]', this.colorIndex > -1 ? this.selected[this.colorIndex] : '');
      } else if (!sizeMsg.hasAttribute('data-guidance') || this.selected[this.sizeIndex]) {
        sizeMsg.textContent = '';
        sizeMsg.removeAttribute('data-guidance');
      }
    }

    this.renderCta();

    // Gallery follows a deliberate colour change.
    if (opts.colorChanged && rep && rep.mediaId) {
      document.dispatchEvent(new CustomEvent('variant:imageChanged', {
        detail: { variantId: rep.id, mediaId: rep.mediaId }
      }));
    }

    // A complete deliberate choice becomes shareable and survives reload/back.
    if (!opts.initial && variant && window.history && history.replaceState) {
      var url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      history.replaceState(history.state, '', url.toString());
    }

    this.renderSticky();
  };

  PdpV1.prototype.markAvailability = function () {
    var self = this;
    this.groups.forEach(function (g) {
      var i = parseInt(g.getAttribute('data-option-index'), 10);
      // A colour is crossed out only when it's sold out in every size; the
      // size chips (and the size message) say which sizes a colour lacks.
      var isColor = i === self.colorIndex;
      qsa(g, 'input[type=radio]').forEach(function (input) {
        var trial = isColor ? self.selected.map(function () { return null; }) : self.selected.slice();
        trial[i] = input.value;
        var candidates = self.data.variants.filter(function (v) {
          return v.options.every(function (o, k) { return trial[k] === null || trial[k] === o; });
        });
        var exists = candidates.length > 0;
        var anyAvailable = candidates.some(function (v) { return v.available; });
        var label = input.nextElementSibling;
        if (!label) return;
        label.classList.toggle('is-soldout', exists && !anyAvailable);
        label.classList.toggle('is-unavailable', !exists);
        var note = qs(label, '[data-soldout-note]');
        if (note) note.textContent = !exists ? ' — ' + self.strings.unavailable : (!anyAvailable ? ' — ' + self.strings.soldOut : '');
      });
    });
  };

  PdpV1.prototype.ctaState = function () {
    if (this.pending) return { label: this.strings.adding, disabled: true, busy: true };
    if (!this.complete()) {
      return { label: this.defaultLabel, disabled: false, needsSize: true };
    }
    if (!this.variant) return { label: this.strings.unavailable, disabled: true };
    if (!this.variant.available) return { label: this.strings.soldOut, disabled: true };
    return { label: this.defaultLabel, disabled: false };
  };

  PdpV1.prototype.renderCta = function () {
    if (!this.cta) return;
    var st = this.ctaState();
    this.ctaLabel.textContent = this.justAdded ? this.strings.added : st.label;
    // Pending uses aria-disabled so focus stays on the button.
    this.cta.disabled = !!st.disabled && !st.busy;
    if (st.busy) this.cta.setAttribute('aria-disabled', 'true'); else this.cta.removeAttribute('aria-disabled');
    this.cta.classList.toggle('is-loading', !!st.busy);
  };

  PdpV1.prototype.showSizeGuidance = function () {
    var g = this.groups.find(function (x) { return x.getAttribute('data-option-kind') === 'size'; }) ||
      this.groups.find(function (x) { return !qs(x, 'input:checked'); });
    if (!g) return;
    var msg = qs(g, '[data-pdp-v1-size-msg]');
    if (msg) {
      msg.textContent = g.getAttribute('data-option-kind') === 'size'
        ? (this.strings.sizeRequired || this.strings.chooseSize)
        : this.strings.chooseOption;
      msg.setAttribute('data-guidance', '');
    }
    g.classList.add('needs-choice');
    scrollToEl(g);
    var first = qs(g, 'input:not(:disabled)');
    if (first) first.focus({ preventScroll: true });
  };

  PdpV1.prototype.submit = function () {
    if (this.pending) return;
    if (!this.complete()) { this.showSizeGuidance(); return; }
    if (!this.variant || !this.variant.available) return;
    if (!this.recipientValid()) return;

    var self = this;
    var submitted = this.variant;
    this.pending = true;
    this.setMessage('');
    this.renderCta();
    this.renderSticky();

    var body = new FormData(this.form);
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    fetch(root + 'cart/add.js', {
      method: 'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
      body: body
    })
      .then(function (res) {
        return res.json().catch(function () { return {}; }).then(function (json) {
          if (!res.ok || json.status) {
            // description is a string, except for field errors (e.g. a gift card
            // recipient email), where it is an object; message is always a string.
            var desc = typeof json.description === 'string' ? json.description : '';
            throw new Error(desc || json.message || self.strings.error);
          }
          return json;
        });
      })
      .then(function (item) {
        if (self.destroyed) return;
        self.pending = false;
        self.justAdded = true;
        self.renderCta();
        clearTimeout(self.addedTimer);
        self.addedTimer = setTimeout(function () { self.justAdded = false; self.renderCta(); self.renderSticky(); }, 2000);
        // PUB_SUB_EVENTS and publish are top-level declarations in constants.js /
        // pubsub.js (not window properties), so reference them by name.
        try {
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', productVariantId: submitted.id, cartData: item });
          }
        } catch (err) {}
        openCart();
      })
      .catch(function (err) {
        if (self.destroyed) return;
        self.pending = false;
        self.renderCta();
        self.setMessage(err && err.message && err.message !== 'Failed to fetch' ? err.message : self.strings.error, true);
      })
      .then(function () { if (!self.destroyed) self.renderSticky(); });
  };

  PdpV1.prototype.setMessage = function (text, isError) {
    if (!this.msg) return;
    this.msg.textContent = text || '';
    this.msg.classList.toggle('is-error', !!isError);
  };

  /* ---------- Sticky bar (mobile) ---------- */

  PdpV1.prototype.setupSticky = function () {
    if (!this.sticky || !this.cta) return;
    var self = this;
    this.stickyBtn = qs(this.sticky, '[data-pdp-v1-sticky-cta]');
    this.stickyLabel = qs(this.sticky, '[data-sticky-label]');
    this.stickyPrice = qs(this.sticky, '[data-sticky-price]');
    this.stickySummary = qs(this.sticky, '[data-sticky-summary]');
    this.ctaPassed = false;
    this.inputFocused = false;

    // Scroll-position check rather than IntersectionObserver: an observer only
    // reports changes in intersection, so a jump from below the fold straight
    // past the button (fast fling, anchor link) never registers.
    var target = this.cta.closest('.pdp-v1__atc') || this.cta;
    this.measureCta = function () {
      var bottom = target.getBoundingClientRect().bottom;
      // Hysteresis: show once 8px past, hide once back in view.
      if (bottom < -8) self.ctaPassed = true;
      else if (bottom > 8) self.ctaPassed = false;
    };

    this.on(this.stickyBtn, 'click', function () {
      if (!self.complete()) { self.showSizeGuidance(); return; }
      if (self.variant && self.variant.available && !self.pending) self.submit();
    });

    var raf = 0;
    var schedule = function () {
      if (raf) return;
      raf = requestAnimationFrame(function () { raf = 0; self.measureCta(); self.refreshSticky(); });
    };
    this.on(window, 'scroll', schedule, { passive: true });
    // Overlays (cart drawer, menu, dialogs, popups) open in response to a click
    // or key, or on load (consent, pop-ups). Re-check after those moments rather
    // than observing the whole document for mutations.
    var later = function () {
      schedule();
      [150, 450, 1000].forEach(function (ms) { self.timers.push(setTimeout(schedule, ms)); });
    };
    this.on(document, 'click', later, true);
    this.on(document, 'keyup', later, true);
    this.on(document, 'transitionend', schedule, true);
    this.on(window, 'load', later);
    [1500, 4000].forEach(function (ms) { self.timers.push(setTimeout(schedule, ms)); });
    this.on(document, 'focusin', function (e) {
      self.inputFocused = !!(e.target && e.target.matches && e.target.matches('input[type=text], input[type=email], input[type=tel], input:not([type]), textarea, [contenteditable=true]'));
      schedule();
    });
    this.on(document, 'focusout', function () { self.inputFocused = false; schedule(); });
    this.on(window, 'resize', schedule);
    this.on(window, 'pageshow', schedule);
    if (mobileQuery.addEventListener) {
      mobileQuery.addEventListener('change', schedule);
      this.cleanup.push(function () { mobileQuery.removeEventListener('change', schedule); });
    }
    this.measureCta();
    this.renderSticky();
    this.refreshSticky();
  };

  PdpV1.prototype.refreshSticky = function () {
    if (!this.sticky) return;
    var show = mobileQuery.matches && this.ctaPassed && !this.inputFocused && !overlayOpen();
    if (this.sticky.hidden === !show) return;
    this.sticky.hidden = !show;
    document.documentElement.classList.toggle('pdp-v1-sticky-visible', show);
  };

  PdpV1.prototype.renderSticky = function () {
    if (!this.sticky || !this.stickyBtn) { return; }
    var rep = this.variant || this.representative();
    if (this.stickyPrice) this.stickyPrice.innerHTML = rep.priceHtml;
    var parts = [];
    var self = this;
    if (this.colorIndex > -1 && this.selected[this.colorIndex]) parts.push(this.selected[this.colorIndex]);
    if (this.sizeIndex > -1) parts.push(this.selected[this.sizeIndex] || this.strings.chooseSize);
    this.otherIndexes.forEach(function (i) { if (self.selected[i]) parts.push(self.selected[i]); });
    if (this.stickySummary) this.stickySummary.textContent = parts.join(' · ');
    var st = this.ctaState();
    var label = this.justAdded ? this.strings.added : (st.needsSize ? this.chooseLabel : st.label);
    this.stickyLabel.textContent = label;
    this.stickyBtn.disabled = !!st.disabled && !st.busy;
    if (st.busy) this.stickyBtn.setAttribute('aria-disabled', 'true'); else this.stickyBtn.removeAttribute('aria-disabled');
    this.stickyBtn.classList.toggle('is-loading', !!st.busy);
  };

  /* ---------- Size guide dialog ---------- */

  PdpV1.prototype.setupGuide = function () {
    var dlg = qs(this.root, '[data-pdp-v1-guide]');
    var opener = qs(this.root, '[data-pdp-v1-guide-open]');
    if (!dlg || !opener || typeof dlg.showModal !== 'function') return;
    var self = this;
    var lastFocus = null;
    this.on(opener, 'click', function () { lastFocus = opener; dlg.showModal(); });
    qsa(dlg, '[data-pdp-v1-guide-close]').forEach(function (b) {
      self.on(b, 'click', function () { dlg.close(); });
    });
    // Click on the backdrop closes.
    this.on(dlg, 'click', function (e) { if (e.target === dlg) dlg.close(); });
    this.on(dlg, 'close', function () { if (lastFocus) lastFocus.focus({ preventScroll: true }); });
  };

  PdpV1.prototype.destroy = function () {
    this.destroyed = true;
    clearTimeout(this.addedTimer);
    this.timers.forEach(clearTimeout);
    this.cleanup.forEach(function (fn) { try { fn(); } catch (e) {} });
    this.cleanup = [];
    document.documentElement.classList.remove('pdp-v1-sticky-visible');
    this.root.__pdpV1 = null;
  };

  /* ---------- Page-level wiring ---------- */

  function initAll(scope) {
    qsa(scope || document, '[data-pdp-v1]').forEach(function (root) {
      if (root.__pdpV1) return;
      try {
        root.__pdpV1 = new PdpV1(root);
        instances.push(root.__pdpV1);
      } catch (e) {
        if (window.console) console.error('[pdp-v1]', e);
      }
    });
  }

  if (window.__pdpV1Wired) { initAll(); return; }
  window.__pdpV1Wired = true;

  // "Purchase area on this page" tiles and other same-page links.
  document.addEventListener('click', function (e) {
    var link = e.target.closest && e.target.closest('[data-pdp-v1-scroll="purchase"]');
    if (!link) return;
    var buy = document.querySelector('[data-pdp-v1] .pdp-v1__buy');
    if (!buy) return; // No V1 purchase area on this page: follow the href.
    e.preventDefault();
    scrollToEl(buy);
    var focusTarget = buy.querySelector('.pdp-v1__option input:checked') || buy.querySelector('.pdp-v1__option input') || buy;
    focusTarget.focus({ preventScroll: true });
  });

  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
    if (window.ProductGallery && typeof window.ProductGallery.initAll === 'function') window.ProductGallery.initAll();
  });
  document.addEventListener('shopify:section:unload', function (e) {
    qsa(e.target, '[data-pdp-v1]').forEach(function (root) {
      if (root.__pdpV1) {
        var inst = root.__pdpV1;
        inst.destroy();
        instances = instances.filter(function (x) { return x !== inst; });
      }
    });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }
})();
